import { env } from '@/env.mjs'
import { useAppDispatch, useAppSelector } from '@/lib/store'
import {
  setCookieConsent,
  setFacebookIntegrationConsent,
  setGoogleIntegrationConsent,
  setMicrosoftIntegrationConsent,
  updateConsentVersion
} from '@/lib/store/core/privacy'
import { selectPrivacySettings } from '@/lib/store/selectors'
import Cookies from 'js-cookie'
import { useCallback, useEffect, useRef, useState } from 'react'

/** Bump this when consent requirements change to re-prompt users. */
const CURRENT_CONSENT_VERSION = '1.1'

type ConsentValue = 'granted' | 'denied'
type ConsentState = Record<'analytics_storage' | 'ad_storage' | 'ad_user_data' | 'ad_personalization', ConsentValue>

/**
 * Consent commands must reach the container as a gtag call, which is an
 * `arguments` object pushed onto window.dataLayer. The container is injected
 * at the edge and defines no gtag function on the page, so define the
 * standard shim here when it is missing. Without this the update is a no-op
 * and the opt-out never reaches GTM.
 */
function gtagConsentUpdate(state: ConsentState) {
  if (typeof window === 'undefined') return
  const w = window as Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void }
  w.dataLayer = w.dataLayer || []
  if (!w.gtag) {
    w.gtag = function gtag() {
      w.dataLayer!.push(arguments)
    }
  }
  w.gtag('consent', 'update', state)
}

export function useConsent() {
  const dispatch = useAppDispatch()
  const storedPrivacyState = useAppSelector(selectPrivacySettings)
  // The first render holds the granted defaults even for a visitor who opted
  // out; sending those to GTM would undo the head snippet's denied default.
  const [storedChoiceApplied, setStoredChoiceApplied] = useState(false)
  const choiceMade = useRef(false)
  const privacyState = storedPrivacyState ?? {
    // Granted by default, matching the slice's initial state: implied consent
    // with the footer cookie preferences link as the opt-out
    // (site-tracking-contract.md section 8, option a). A stored choice still
    // wins, dispatched over this on mount.
    cookieConsent: {
      analytics: true,
      advertising: true,
      functional: false,
      necessary: true
    },
    microsoftIntegration: {
      dataAccess: false,
      dataSharing: false,
      emailAccess: false,
      calendarAccess: false,
      fileAccess: false
    },
    googleIntegration: {
      dataAccess: false,
      dataSharing: false,
      emailAccess: false,
      calendarAccess: false,
      fileAccess: false
    },
    facebookIntegration: {
      dataAccess: false,
      dataSharing: false,
      emailAccess: false,
      calendarAccess: false,
      fileAccess: false
    },
    consentVersion: '1.0',
    lastUpdated: new Date().toISOString()
  }

  // Load initial privacy state from cookie
  useEffect(() => {
    const storedConsent = Cookies.get(env.NEXT_PUBLIC_COOKIE_CONSENT_KEY)
    if (storedConsent) {
      try {
        const { settings, integrations, version, lastUpdated } = JSON.parse(storedConsent)

        // Update state with stored settings
        dispatch(setCookieConsent(settings))
        if (integrations) {
          dispatch(setMicrosoftIntegrationConsent(integrations.microsoft))
          dispatch(setGoogleIntegrationConsent(integrations.google))
          dispatch(setFacebookIntegrationConsent(integrations.facebook))
        }
        dispatch(updateConsentVersion(version))
      } catch (error) {
        console.error('Failed to parse stored consent:', error)
      }
    }
    setStoredChoiceApplied(true)
  }, [dispatch])

  // Only an explicit choice writes the cookie. Writing from state on mount
  // would store the defaults over a real opt-out: StoreProvider remounts this
  // tree, and the second mount reads that write back as the visitor's choice.
  useEffect(() => {
    if (!choiceMade.current) return

    const consentData = {
      settings: privacyState.cookieConsent,
      integrations: {
        microsoft: privacyState.microsoftIntegration,
        google: privacyState.googleIntegration,
        facebook: privacyState.facebookIntegration
      },
      version: privacyState.consentVersion,
      lastUpdated: privacyState.lastUpdated
    }

    Cookies.set(env.NEXT_PUBLIC_COOKIE_CONSENT_KEY, JSON.stringify(consentData), {
      expires: 365
    })
  }, [privacyState])

  // Initialize GTM/Analytics based on consent
  useEffect(() => {
    if (!storedChoiceApplied) return
    // ad_user_data is what enhanced conversions run under: without it the
    // hashed email and phone the lead forms push are dropped before they reach
    // the Ads account, so it travels with ad_storage rather than separately.
    if (privacyState.cookieConsent.analytics) {
      const advertising = privacyState.cookieConsent.advertising ? 'granted' : 'denied'
      gtagConsentUpdate({
        analytics_storage: 'granted',
        ad_storage: advertising,
        ad_user_data: advertising,
        ad_personalization: advertising
      })
    } else {
      gtagConsentUpdate({
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      })
    }
  }, [storedChoiceApplied, privacyState.cookieConsent.analytics, privacyState.cookieConsent.advertising])

  // Handle Facebook Pixel consent
  useEffect(() => {
    if (privacyState.facebookIntegration.dataAccess) {
      // Initialize Facebook Pixel
      window.fbq?.('consent', 'grant')
    } else {
      window.fbq?.('consent', 'revoke')
    }
  }, [privacyState.facebookIntegration.dataAccess])

  const setMaxPrivacy = useCallback(() => {
    choiceMade.current = true
    const consentSettings = {
      analytics: false,
      advertising: false,
      functional: false,
      necessary: true // Always true as it's required
    }

    const integrationSettings = {
      dataAccess: false,
      dataSharing: false,
      emailAccess: false,
      calendarAccess: false,
      fileAccess: false
    }

    dispatch(setCookieConsent(consentSettings))
    dispatch(setMicrosoftIntegrationConsent(integrationSettings))
    dispatch(setGoogleIntegrationConsent(integrationSettings))
    dispatch(setFacebookIntegrationConsent(integrationSettings))
    dispatch(updateConsentVersion(CURRENT_CONSENT_VERSION))
  }, [dispatch])

  const setMaxConvenience = useCallback(() => {
    choiceMade.current = true
    const consentSettings = {
      analytics: true,
      advertising: true,
      functional: true,
      necessary: true
    }

    const integrationSettings = {
      dataAccess: true,
      dataSharing: true,
      emailAccess: true,
      calendarAccess: true,
      fileAccess: true
    }

    dispatch(setCookieConsent(consentSettings))
    dispatch(setMicrosoftIntegrationConsent(integrationSettings))
    dispatch(setGoogleIntegrationConsent(integrationSettings))
    dispatch(setFacebookIntegrationConsent(integrationSettings))
    dispatch(updateConsentVersion(CURRENT_CONSENT_VERSION))
  }, [dispatch])

  const setCustomConsent = useCallback(
    (
      cookieSettings: Partial<typeof privacyState.cookieConsent>,
      integrationSettings?: {
        microsoft?: Partial<typeof privacyState.microsoftIntegration>
        google?: Partial<typeof privacyState.googleIntegration>
        facebook?: Partial<typeof privacyState.facebookIntegration>
      }
    ) => {
      choiceMade.current = true
      dispatch(setCookieConsent({ ...privacyState.cookieConsent, ...cookieSettings }))

      if (integrationSettings?.microsoft) {
        dispatch(setMicrosoftIntegrationConsent(integrationSettings.microsoft))
      }
      if (integrationSettings?.google) {
        dispatch(setGoogleIntegrationConsent(integrationSettings.google))
      }
      if (integrationSettings?.facebook) {
        dispatch(setFacebookIntegrationConsent(integrationSettings.facebook))
      }

      dispatch(updateConsentVersion(CURRENT_CONSENT_VERSION))
    },
    [dispatch, privacyState]
  )

  const checkConsentRequired = useCallback(() => {
    const storedConsent = Cookies.get(env.NEXT_PUBLIC_COOKIE_CONSENT_KEY)
    if (!storedConsent) return true

    try {
      const { version } = JSON.parse(storedConsent)
      return version !== CURRENT_CONSENT_VERSION
    } catch {
      return true
    }
  }, [])

  return {
    privacyState,
    setMaxPrivacy,
    setMaxConvenience,
    setCustomConsent,
    checkConsentRequired
  }
}
